import { ReactElement, useContext } from 'react';
import { Image } from '@sitecore-jss/sitecore-jss-nextjs';

import { getSharedArray, getSharedProperty } from '../../../getters';
import { SearchMenuFields } from '../../../types';
import { stripHtmlTags } from '../../../utils';
import useFallbackTranslation from '../../../utils/hooks/useFallbackTranslation';
import { SearchContext } from '../providers';
import { Pagination } from './Pagination';
import { SearchTitleHeader } from './SearchTitleHeader';

type ProductResultsProps = {
  searchQuery: string;
  menuItem: SearchMenuFields;
};

type DetailLinkProps = {
  pageUrl: string;
  label: string;
  postfix?: string;
};

const DetailsLink = ({ pageUrl, label, postfix = '' }: DetailLinkProps): ReactElement => {
  const { getURLWithLocalePrefix } = useFallbackTranslation();
  return (
    <>
      {pageUrl ? (
        <a
          href={getURLWithLocalePrefix(pageUrl ? pageUrl + postfix : '/')}
          role="link"
          className="cursor-pointer text-xs font-normal underline underline-offset-4 hover:no-underline"
          aria-label={`Product ${label} link`}
        >
          {label}
        </a>
      ) : null}
    </>
  );
};

export const ProductResults = ({ searchQuery, menuItem }: ProductResultsProps): ReactElement => {
  const { t, getURLWithLocalePrefix } = useFallbackTranslation();
  const searchResultsContext = useContext(SearchContext);
  const { searchResults } = searchResultsContext;

  const { NumberOfResultsPerPage, NumberOfResultsPerPageShared } = menuItem;

  const data = { fields: menuItem };

  const nextLabelArray = getSharedArray(data.fields, 'NextLabel');
  const nextLabel = getSharedProperty(nextLabelArray.fields, 'Phrase')?.value || '';

  const previousLabelArray = getSharedArray(data.fields, 'PreviousLabel');
  const previousLabel = getSharedProperty(previousLabelArray.fields, 'Phrase')?.value || '';

  const productFoundLabelArray = getSharedArray(data.fields, 'ProductsFoundLabel');
  const productFoundLabel = getSharedProperty(productFoundLabelArray.fields, 'Phrase')?.value || '';

  const productResultsLabelArray = getSharedArray(data.fields, 'ProductResultsLabel');
  const productResultsLabel =
    getSharedProperty(productResultsLabelArray.fields, 'Phrase')?.value || '';

  const productInformationLabelArray = getSharedArray(data.fields, 'ProductInformationLabel');
  const productInformationLabel =
    getSharedProperty(productInformationLabelArray.fields, 'Phrase')?.value || '';

  const supportLabelArray = getSharedArray(data.fields, 'SupportLabel');
  const supportLabel = getSharedProperty(supportLabelArray.fields, 'Phrase')?.value || '';

  const accessoriesLabelArray = getSharedArray(data.fields, 'AccessoriesLabel');
  const accessoriesLabel = getSharedProperty(accessoriesLabelArray.fields, 'Phrase')?.value || '';

  const numberOfResultsPerPage = parseInt(
    NumberOfResultsPerPage?.value
      ? (NumberOfResultsPerPage?.value ?? '4')
      : NumberOfResultsPerPageShared?.value || '4'
  );

  const handleNavigation = (url: string) => {
    // replace to router.push after coexistence period.
    window.location.href = getURLWithLocalePrefix(url);
  };
  return (
    <div className="w-full">
      <div className="mb-3 flex flex-col items-center justify-center pt-3">
        <div className="w-full sm:w-4/6 md:w-4/6 lg:w-4/6 xl:w-2/6">
          <SearchTitleHeader
            title={productResultsLabel}
            subtitle={`(${searchResultsContext?.searchResults?.count} ${productFoundLabel})`}
          />
          <ul className="grid grid-cols-1 gap-4 sm:gap-6">
            {searchResults?.items.map((item) => (
              <li key={`product-${item.pageUrl}-${item.title}`}>
                <button
                  onClick={() => handleNavigation(item.pageUrl)}
                  className="flex w-full border-b border-solid border-gray-400 pb-2 no-underline hover:no-underline sm:border-0 sm:pb-0"
                  aria-label={`${t('topmenu.NavigateToProductDetail')} ${item.title}`}
                >
                  <span className="flex w-full flex-col items-center justify-center rounded-lg p-4 hover:bg-beige-50 hover:shadow-shadow10 sm:flex-row sm:items-center md:gap-10">
                    {/* todo: add translation for label */}
                    <Image
                      aria-label={t('topmenu.ProductImage')}
                      className="flex w-36 justify-center xs:w-32"
                      field={{ src: item.images[0] }}
                      alt={item.title}
                    />
                    <span
                      className="flex w-full flex-col items-start justify-center sm:w-2/3"
                      aria-labelledby={`product-title-${item.pageUrl}`}
                    >
                      <span className="flex w-full flex-col items-start">
                        <h2
                          className="w-full truncate text-left text-lg font-bold"
                          aria-label={t('topmenu.ProductTitle')}
                        >
                          {item.title}
                        </h2>
                        <p className="w-full truncate text-left text-xs font-bold">
                          {stripHtmlTags(item.uniqueSellingPoint)}
                        </p>
                      </span>
                      <span className="mt-2 flex w-full flex-col flex-wrap gap-4 text-start lg:flex-row lg:gap-3">
                        <DetailsLink pageUrl={item.pageUrl} label={productInformationLabel} />
                        <DetailsLink pageUrl={item.supportPageUrl} label={supportLabel} />
                        <DetailsLink
                          pageUrl={item.pageUrl}
                          postfix={'#/ACC'}
                          label={accessoriesLabel}
                        />
                      </span>
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex justify-center border-0 pt-2 xs:justify-end sm:border-t sm:border-solid sm:border-gray-400">
            <Pagination
              totalProducts={searchResults.count}
              searchQuery={searchQuery}
              nextLabel={nextLabel}
              previousLabel={previousLabel}
              numberOfResultsPerPage={numberOfResultsPerPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
